import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  DensityToggle,
  explodeCSVAndGetObjectsFromLookup,
  FormSearchResultAdvanced,
  generateLocaleId,
  useDataProvider,
  useLocalization,
  useMasterData,
  useUserAccess,
  useStore,
} from '@ui-framework/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
  ExportButton,
} from '@product-framework/index';
import {
  serviceJobNumber,
  customerName,
  customerServiceRequests,
  customerAddressCity,
  customerAddressState,
  customerAddressPostalCode,
  productType,
  brand,
  customerComplaint,
  appointmentDate,
  serviceJobStatus,
} from './accessorFunctions';
import { listSchema, searchSASchema, searchSPSchema } from '../../schemas';
import validationSchema from './validations';

function ListHeaderContent({ table }) {
  return (
    <Box textAlign="right">
      <DensityToggle sx={{ display: 'none' }} onDensityChange={args => args} />
      <ExportButton
        table={table}
        permissionName="CAN_EXPORT_SERVICE_JOBS"
        exportUrl="/sxcl/v1/service-jobs/operation/export"
      />
    </Box>
  );
}

function ServiceJobList() {
  const defaultListTitle = {
    id: 'SERVICEJOB_LIST_HEADING',
    defaultMessage: 'Jobs',
  };
  const [searchUrlParams] = useSearchParams();
  const urlShowSearch = (searchUrlParams.get('view') || '') === 'search';

  const { formatMessage } = useLocalization();
  const tableRef = useRef();
  const masterData = useMasterData();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(urlShowSearch);
  const [resultsFound, setResultsFound] = useState(0);
  const store = useStore();

  const userAccess = useUserAccess();
  const searchSchema =
    userAccess?.isSA() || userAccess?.isAdmin()
      ? searchSASchema
      : searchSPSchema;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-jobs`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEJOBS_LIST_BREADCRUMB',
        defaultMessage: 'List Jobs',
      }),
      icon: IconLibrary.ServiceJobs,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_NEW_SERVICEJOB_STACKABLE_BUTTON',
        defaultMessage: 'Create Job',
      }),
      Icon: IconLibrary.ServiceJobCreate,
      permissionName: 'CREATE_JOB',
      variant: 'outlined',
      onClick: () => {
        navigate('/servicejobs/preregister');
      },
    },
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      permissionName: 'SEARCH_JOB',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceJobList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    return searchedParams;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceJobList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    ref: tableRef,
    accessorFunctions: {
      serviceJobNumber,
      serviceJobStatus,
      customerName,
      customerServiceRequests,
      appointmentDate,
      customerAddressCity,
      customerAddressState,
      customerAddressPostalCode,
      brand,
      productType,
      customerComplaint,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'DESC',
    sortBy: 'createdDateTime',
  };

  const searchProps = {
    customComponents: FormFields,
    storeName: 'ServiceJobList',
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset('ServiceJobList');
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    initialValues: {},
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'claimStatus': {
        const statuses = explodeCSVAndGetObjectsFromLookup(
          value,
          masterData.claimStatuses,
        );
        return statuses.map(s => s.label).join(', ');
      }
      case 'serviceJobStatus': {
        const statuses = explodeCSVAndGetObjectsFromLookup(
          value,
          masterData.jobStatus,
        );
        return statuses.map(s => s.label).join(', ');
      }
      case 'dispatchStatus': {
        const statuses = explodeCSVAndGetObjectsFromLookup(
          value,
          masterData.dispatch,
        );
        return statuses.map(s => s.label).join(', ');
      }
      default:
        return null;
    }
  };

  const searchParamsProps = {
    preProcessSearchValues,
    storeName: 'ServiceJobList',
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  const getPageTitle = () => {
    const searchTitle = searchUrlParams.get('searchTitle') || '';
    const currentStore = store.domain.list.get('ServiceJobList');
    if (searchTitle && currentStore?.filters?.length > 0) {
      return {
        id: generateLocaleId(searchTitle),
        defaultMessage: searchTitle,
      };
    }
    return defaultListTitle;
  };

  useEffect(() => {
    setShowSearch(urlShowSearch);
  }, [urlShowSearch]);

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'SERVICEJOB_SEARCH_HEADING',
            defaultMessage: 'Search Jobs',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        HeaderContent={<ListHeaderContent table={listProps.ref} />}
        label={formatMessage(getPageTitle())}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceJobList);
